import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiService } from './api.service';

/**
 * Service pour la gestion de l'authentification API V2
 * 
 * L'API V2 (Stripe) nécessite un token JWT obtenu via l'API V1
 * Endpoint: POST /auth/authentication_api_v2 (sur le backend API V1)
 * Retour: { jwt: '<token_string>' }
 * 
 * Le token est alors utilisé dans les appels API V2 avec le format:
 * Authorization: Bearer <token>
 */
@Injectable({
  providedIn: 'root'
})
export class ApiV2AuthenticationService {

  private tokenCache: { token: string; timestamp: number } | null = null;
  private readonly TOKEN_EXPIRY_MS = 1 * 60 * 1000; // 1 minute (token expire en 1 minutes)

  constructor(private apiService: ApiService) { }

  /**
   * Récupère un token JWT pour les appels API V2
   * 
   * Le token est mis en cache pour éviter des appels répétés
   * 
   * @returns Promise<string> - Le token JWT valide
   * @throws Error si la récupération échoue
   * 
   * @example
   * const token = await this.apiV2AuthService.getApiV2Token();
   * // Utiliser ensuite dans les appels API V2 avec Bearer format
   */
  getApiV2Token(): Observable<string> {
    // Retourner le token en cache s'il est toujours valide
    if (this.tokenCache && !this.isTokenExpired(this.tokenCache.timestamp)) {
      console.log('♻️ Using cached API V2 token');
      return new Observable(observer => {
        observer.next(this.tokenCache!.token);
        observer.complete();
      });
    }

    console.log('🔑 Fetching new API V2 JWT token');

    // Récupérer le token via l'API V1
    return this.apiService.post('auth/authentication_api_v2', {}).pipe(
      map((response: any) => {
        if (response && response.jwt) {
          console.log('✅ API V2 JWT token retrieved');

          // Mettre en cache le token
          this.tokenCache = {
            token: response.jwt,
            timestamp: Date.now()
          };

          return response.jwt;
        } else {
          throw new Error('No JWT in response from /auth/authentication_api_v2');
        }
      }),
      catchError(error => {
        console.error('❌ Error retrieving API V2 token:', error);
        return throwError(() => new Error(`Failed to retrieve API V2 token: ${error.message}`));
      })
    );
  }

  /**
   * Récupère le token comme Promise (utile pour async/await)
   * 
   * @returns Promise<string> - Le token JWT valide
   * @throws Error si la récupération échoue
   * 
   * @example
   * try {
   *   const token = await this.apiV2AuthService.getApiV2TokenAsPromise();
   * } catch (error) {
   *   console.error('Token retrieval failed', error);
   * }
   */
  getApiV2TokenAsPromise(): Promise<string> {
    return new Promise((resolve, reject) => {
      this.getApiV2Token().subscribe({
        next: (token) => resolve(token),
        error: (error) => reject(error)
      });
    });
  }

  /**
   * Invalide le cache du token
   * Utile pour forcer une nouvelle récupération
   */
  invalidateTokenCache(): void {
    this.tokenCache = null;
    console.log('🗑️ API V2 token cache invalidated');
  }

  /**
   * Vérifie si un token en cache est expiré
   * @param timestamp - Timestamp de création du token
   * @returns boolean - true si expiré, false sinon
   */
  private isTokenExpired(timestamp: number): boolean {
    return Date.now() - timestamp > this.TOKEN_EXPIRY_MS;
  }
}
